// Tela Início (desktop) — pedido 2026-09-03: "criar o menu novo da tela de
// início em cima do menu do mapa, igual [n]o visual do app pwa" + referência
// visual (agreed-lavender-oxhxaszw.edgeone.dev/file.png): um painel-resumo
// acima do Mapa no menu, mesmo espírito da aba Início que já existe no PWA
// (ver InicioPanel em components/mobile-app.tsx) — aqui é a MESMA lógica
// real (frota, veículo em destaque, resumo do dia via
// useVehicleSummary/Stops/Trips, telefone de emergência real), só que no
// layout de card do desktop em vez do layout de celular.
//
// Deliberadamente SEM "Avisos da empresa"/"Solicitar assistência"/"Minhas
// faturas" que apareciam na referência — não existe hoje nenhum dado real
// nem destino de desktop pra essas três (avisos da empresa não tem backend
// nenhum ainda; faturas/assistência são fluxos de autoatendimento do
// CLIENTE no PWA, não fazem sentido pro operador que usa este console) —
// nunca inventar conteúdo só pra bater com a imagem de referência.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { startOfDay } from "date-fns";
import { toast } from "sonner";
import {
  Gauge,
  Route as RouteIcon,
  Zap,
  Activity,
  MapPin,
  ChevronRight,
  Lock,
  FileText,
  LandPlot,
  Plus,
  CheckCircle2,
  Settings2,
  Users as UsersIcon,
  ShieldCheck,
  ShieldAlert,
  Car,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import { usePermission, useAnyPermission } from "@/hooks/use-permission";
import {
  useVehicles,
  useVehicleImages,
  useVehicleSummary,
  useVehicleStops,
  useVehicleTrips,
  useQuickAccessCatalog,
  useMyQuickAccess,
  useUpdateMyQuickAccess,
  useVehicleMetadata,
  useBlockCommandSmsFallback,
} from "@/hooks/queries";
import { useReverseGeocode } from "@/hooks/use-reverse-geocode";
import { resolveGpsDriveUrl, GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import {
  greetingFor,
  iconUrlFor,
  statusColorFor,
  statusText,
  formatShortDuration,
  InicioSky,
  useVehicleBlockAction,
} from "@/components/mobile-app";
import { VehicleBlockConfirmDialog } from "@/components/vehicle-block-confirm-dialog";
import {
  describeWeatherCode,
  isRainCode,
  useWeather,
  useWeatherCoords,
  weatherBucket,
} from "@/hooks/use-weather";
import { VehicleSendCommandDialog } from "@/components/vehicle-send-command-dialog";
import { StreetViewPanorama } from "@/components/street-view-panorama";
import { RouteHistoryCenter } from "@/components/route-history-center";
import { FleetInsightsCharts, FleetInsightsTop } from "@/components/inicio/fleet-insights";
import type { Vehicle, VehicleStatus } from "@/types";
import { cn } from "@/lib/utils";
import { isRestrictedAccountType } from "@/lib/account-type";
import { LicensePlate } from "@/components/license-plate";
import { timeWithAgo } from "@/lib/time-ago";

// Mesmo default do backend (QUICK_ACCESS_DEFAULT_TILES em
// quick-access-catalog.constants.ts) — usado enquanto o usuário nunca
// configurou nada (tiles: []), pra ninguém abrir a tela pela primeira vez
// com o card vazio.
// Ordem do carrossel do Início: online (em movimento) → parado → offline.
const CAROUSEL_STATUS_RANK: Record<VehicleStatus, number> = { online: 0, idle: 1, offline: 2 };
const DEFAULT_QUICK_ACCESS_TILES = ["tracking", "block", "send_command", "reports", "geofences"];

export const Route = createFileRoute("/inicio")({
  component: InicioPage,
  head: () => ({ meta: [{ title: "Início — Wp Rastreamento" }] }),
});

// Carrossel de avatares (pedido 2026-10-01): TODOS os veículos, sem o
// limite antigo de 20, e sem tags (rastreador tipo "tag" não é veículo pra
// escolher aqui). Fotos com loading="lazy" — só baixam as que aparecem.

// Pedido 2026-09-08: "quando entrar no app primeira tela que tem que abrir
// e a tela inicial e não o mapa" — chave usada só pra distinguir "acabei de
// entrar no app" (ainda não passou pelo Início nesta aba) de "já estou
// navegando e cliquei em Mapa de propósito" (ver beforeLoad em
// routes/index.tsx#Route, que só redireciona pra cá quando esta chave ainda
// não existe). sessionStorage (não localStorage) de propósito — cada aba
// nova volta a contar como "acabei de entrar".
const ENTERED_APP_SESSION_KEY = "gpsdrive.enteredApp";

function InicioPage() {
  const { user, gpsDriveUser } = useAuth();
  useEffect(() => {
    try {
      sessionStorage.setItem(ENTERED_APP_SESSION_KEY, "1");
    } catch {
      // Storage indisponível (aba privada/bloqueada) — sem isso o pior
      // caso é o redirect de routes/index.tsx acontecer de novo a cada
      // clique em "Mapa", nunca uma quebra funcional.
    }
  }, []);
  // Mesma regra do Mapa (ver app-sidebar.tsx): Início nunca fica atrás de
  // bloqueio de permissão — sem devices.view só mostra a frota vazia.
  const canViewDevices = usePermission("devices", "view");
  const canCreateDevices = usePermission("devices", "create");
  const canSendCommand = usePermission("commands", "send_command");
  const canViewUsers = usePermission("users", "view");
  const canSeeAdmin = useAnyPermission("administracao", [
    "identidade_visual",
    "conta_integracoes",
    "comunicacao",
    "notificacoes",
    "formularios",
  ]);
  // isLoading (não isFetching): fica true só antes do PRIMEIRO sucesso da
  // query — sem isso, "0 veículos na frota" + "Nenhum veículo cadastrado"
  // apareciam por 1-2s a cada F5/login antes do fetch inicial responder
  // (bug relatado 2026-09-04).
  const { data: rawVehicles = [], isLoading: vehiclesLoading } = useVehicles();
  const vehicles = canViewDevices ? rawVehicles : [];

  const [featuredId, setFeaturedId] = useState<string | null>(null);
  // Cliente vê as próprias tags (depois dos veículos) — mesma regra do
  // Início do app (mobile-app.tsx InicioPanel, 2026-10-05).
  const isClientAccount = isRestrictedAccountType(gpsDriveUser?.accountType);
  // Pedido 2026-10-12: online primeiro no carrossel — em movimento, depois
  // parado/conectado, por último offline (ordem original mantida em cada
  // grupo; tags do cliente continuam no fim).
  const carouselVehicles = useMemo(() => {
    const byOnline = (list: typeof vehicles) =>
      [...list].sort((a, b) => CAROUSEL_STATUS_RANK[a.status] - CAROUSEL_STATUS_RANK[b.status]);
    return isClientAccount
      ? [...byOnline(vehicles.filter((v) => !v.isTag)), ...byOnline(vehicles.filter((v) => v.isTag))]
      : byOnline(vehicles.filter((v) => !v.isTag));
  }, [vehicles, isClientAccount]);
  const featured = carouselVehicles.find((v) => v.id === featuredId) ?? carouselVehicles[0] ?? null;

  // "Bloqueio veicular" (pedido 2026-09-22, igual ao app): abre o popup de
  // bloquear/desbloquear do veículo em destaque — mesmo envio do app
  // (useVehicleBlockAction: GPRS + fallback SMS, recusa offline, toasts).
  const canLock = usePermission("devices", "lock");
  const canUnlock = usePermission("devices", "unlock");
  const { data: featuredMetadata } = useVehicleMetadata(featured?.id);
  const featuredSmsFallback = useBlockCommandSmsFallback(featuredMetadata?.protocol, featuredMetadata?.trackerModel);
  const featuredBlock = useVehicleBlockAction(featured, featuredSmsFallback);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);

  // Banner do clima (pedido 2026-09-22, mesmo clima/céu do app): local do
  // navegador; sem permissão de localização, posição do veículo em destaque.
  const weatherFallback = useMemo(
    () =>
      featured && Number.isFinite(featured.lat) && Number.isFinite(featured.lng) && (featured.lat !== 0 || featured.lng !== 0)
        ? { lat: featured.lat, lon: featured.lng }
        : null,
    [featured?.lat, featured?.lng],
  );
  const weather = useWeather(weatherFallback);
  const weatherCoords = useWeatherCoords(weatherFallback);
  const weatherCity = useReverseGeocode(weatherCoords?.lat, weatherCoords?.lon)?.split(" · ").pop()?.trim();
  const weatherMeta = weather.data ? describeWeatherCode(weather.data.weatherCode, weather.data.isDay) : null;
  const skyBucket = weather.data ? weatherBucket(weather.data.weatherCode, weather.data.isDay) : null;
  const raining = !!weather.data && isRainCode(weather.data.weatherCode);
  const storm = !!weather.data && weather.data.weatherCode >= 95;

  const { data: vehicleImagesMap } = useVehicleImages(carouselVehicles.map((v) => v.id));
  const address = useReverseGeocode(featured?.lat, featured?.lng, { stableKey: featured?.id });
  const greeting = useMemo(() => greetingFor(user?.name?.split(" ")[0]), [user?.name]);

  // Janela "hoje", mesmo cálculo do Centro de Histórico / Início do PWA.
  const today = useMemo(() => {
    const now = new Date();
    return { from: startOfDay(now).toISOString(), to: now.toISOString() };
  }, []);
  const summaryQuery = useVehicleSummary(featured?.id, today.from, today.to, !!featured);
  const stopsQuery = useVehicleStops(featured?.id, today.from, today.to, !!featured);
  const tripsQuery = useVehicleTrips(featured?.id, today.from, today.to, !!featured);
  const summary = summaryQuery.data?.[0];
  const validStops = (stopsQuery.data ?? []).filter(
    (s) => Number.isFinite(s.lat) && Number.isFinite(s.lng) && s.durationMs > 0,
  );
  const movingMs = (tripsQuery.data ?? []).reduce((s, t) => s + t.durationMs, 0);

  const featuredImageUrls = featured ? vehicleImagesMap?.[featured.id] : undefined;
  const featuredPhotoUrl = featuredImageUrls
    ? resolveGpsDriveUrl(featuredImageUrls["256"] ?? featuredImageUrls.original)
    : undefined;

  // Aviso "N offline" do card Meus veículos — sem quem nunca posicionou
  // (esses têm fatia própria no Painel, ver components/inicio/fleet-insights).
  const offline = vehicles.filter((v) => v.hasPosition !== false && v.status === "offline").length;

  const [commandVehicle, setCommandVehicle] = useState<Vehicle | null>(null);
  const [historyVehicleId, setHistoryVehicleId] = useState<string | null>(null);
  const [configOpen, setConfigOpen] = useState(false);

  // Escolha do PRÓPRIO usuário, salva no backend (nunca localStorage — ver
  // gpsdrive-quick-access.service.ts). [] (nunca configurou) cai no mesmo
  // default de sempre; nunca afeta o "Acesso rápido" do PWA, que é fixo.
  const { data: myQuickAccess } = useMyQuickAccess();
  const quickAccessKeys =
    myQuickAccess?.tiles && myQuickAccess.tiles.length > 0
      ? myQuickAccess.tiles
      : DEFAULT_QUICK_ACCESS_TILES;

  const quickAccessTiles = useMemo(
    () =>
      featured
        ? quickAccessKeys
            .map((key) =>
              renderQuickAccessTile(key, {
                featured,
                canBlock: canLock || canUnlock,
                onBlock: () => setBlockDialogOpen(true),
                canSendCommand,
                canViewUsers,
                canSeeAdmin,
                onSendCommand: () => setCommandVehicle(featured),
                onOpenHistory: () => setHistoryVehicleId(featured.id),
              }),
            )
            .filter((t): t is NonNullable<typeof t> => t !== null)
        : [],
    [quickAccessKeys, featured, canSendCommand, canViewUsers, canSeeAdmin, canLock, canUnlock],
  );

  return (
    <div className="space-y-6 p-6">
      {/* Banner com o céu do clima atual (mesmo InicioSky do app) — sem
          clima disponível ainda, cai no cabeçalho simples de sempre. */}
      {weather.data && weatherMeta && skyBucket ? (
        <div
          className={cn(
            "relative isolate flex min-h-[132px] items-center justify-between gap-4 overflow-hidden rounded-2xl px-6 py-5 text-white shadow-sm",
            // Fundo de reserva (o céu claro some no tema escuro, ver InicioSky)
            skyBucket === "sol" && "bg-sky-600",
            skyBucket === "nuvem" && "bg-slate-500",
            skyBucket === "chuva" && "bg-slate-600",
            skyBucket === "tempestade" && "bg-slate-800",
            skyBucket === "noite" && "bg-indigo-950",
          )}
        >
          <InicioSky bucket={skyBucket} raining={raining} storm={storm} />
          <div className="min-w-0 [text-shadow:0_1px_3px_rgba(0,0,0,0.35)]">
            <h1 className="truncate text-2xl font-bold tracking-tight">
              {greeting.text} <span aria-hidden>{greeting.emoji}</span>
            </h1>
            <p className="text-sm text-white/90">Bem-vindo de volta! Aqui está o resumo da sua frota agora.</p>
          </div>
          <div className="flex shrink-0 items-center gap-3 rounded-2xl border border-white/25 bg-white/15 px-4 py-3 backdrop-blur-md">
            <weatherMeta.icon
              className={cn(
                "h-11 w-11 shrink-0 drop-shadow",
                skyBucket === "sol" && "wx-spin fill-amber-300 text-amber-400",
                skyBucket === "nuvem" && "wx-float fill-slate-200 text-white",
                skyBucket === "chuva" && "wx-float fill-slate-300 text-white",
                skyBucket === "tempestade" && "wx-glow fill-slate-400 text-white",
                skyBucket === "noite" && "wx-glow fill-white text-white",
              )}
              strokeWidth={1.5}
              aria-hidden
            />
            <div className="[text-shadow:0_1px_2px_rgba(0,0,0,0.3)]">
              <div className="text-2xl font-bold leading-none">{weather.data.temperatureC}°C</div>
              <div className="mt-1 text-sm font-medium text-white/90">{weatherMeta.label}</div>
              <div className="mt-0.5 flex items-center gap-1 text-xs text-white/80">
                <MapPin className="h-3 w-3" />
                {weatherCity ?? "Sua localização"} · máx {weather.data.maxC}° mín {weather.data.minC}°
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {greeting.text} <span aria-hidden>{greeting.emoji}</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            Bem-vindo de volta! Aqui está o resumo da sua frota agora.
          </p>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="text-base">Meus veículos</CardTitle>
                <CardDescription>
                  {vehiclesLoading ? "Carregando frota…" : `${vehicles.length} veículos na frota`}
                </CardDescription>
              </div>
              {!vehiclesLoading && vehicles.length > 0 &&
                (offline === 0 ? (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Tudo certo
                  </span>
                ) : (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-600 dark:text-amber-400">
                    {offline} offline
                  </span>
                ))}
            </CardHeader>
            <CardContent>
              <div className="flex gap-3 overflow-x-auto pb-1">
                {canCreateDevices && (
                  <Link
                    to="/vehicles"
                    search={{ new: 1 }}
                    className="flex shrink-0 flex-col items-center gap-1.5"
                  >
                    <span className="grid h-14 w-14 place-items-center rounded-full border-2 border-dashed border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary">
                      <Plus className="h-5 w-5" />
                    </span>
                    <span className="text-xs font-medium text-muted-foreground">Adicionar</span>
                  </Link>
                )}
                {carouselVehicles.map((v) => {
                  const imgs = vehicleImagesMap?.[v.id];
                  const photoUrl = imgs ? resolveGpsDriveUrl(imgs["128"] ?? imgs.original) : undefined;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setFeaturedId(v.id)}
                      className="flex shrink-0 flex-col items-center gap-1.5"
                    >
                      <span
                        className={cn(
                          "grid h-14 w-14 place-items-center overflow-hidden rounded-full border-2 bg-muted",
                          featured?.id === v.id ? "border-primary" : "border-transparent",
                        )}
                      >
                        <img
                          src={photoUrl ?? iconUrlFor(v)}
                          alt=""
                          loading="lazy"
                          className={photoUrl ? "h-full w-full object-cover" : "h-8 w-8 object-contain"}
                        />
                      </span>
                      <span className="max-w-[64px] truncate text-xs font-medium text-muted-foreground">
                        {v.name.split(" ")[0]}
                      </span>
                    </button>
                  );
                })}
                {vehiclesLoading ? (
                  <p className="py-2 text-sm text-muted-foreground">Carregando…</p>
                ) : (
                  vehicles.length === 0 && (
                    <p className="py-2 text-sm text-muted-foreground">Nenhum veículo cadastrado.</p>
                  )
                )}
              </div>
            </CardContent>
          </Card>

          {featured && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex gap-4">
                  <div className="relative h-20 w-28 shrink-0 overflow-hidden rounded-lg bg-muted">
                    <img
                      src={featuredPhotoUrl ?? iconUrlFor(featured)}
                      alt=""
                      className={
                        featuredPhotoUrl ? "h-full w-full object-cover" : "h-full w-full object-contain p-2"
                      }
                    />
                    <span
                      className="absolute bottom-1.5 left-1.5 h-3 w-3 rounded-full border-2 border-card"
                      style={{ background: statusColorFor(featured) }}
                      title={statusText(featured)}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate text-base font-bold">{featured.name}</h3>
                      <LicensePlate plate={featured.plate} size="xs" />
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <div className="text-xs text-muted-foreground">Velocidade</div>
                        <div className="font-semibold">{Math.round(featured.speed ?? 0)} km/h</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Bateria</div>
                        <div className="font-semibold">
                          {featured.powerVoltage != null
                            ? `${featured.powerVoltage.toFixed(1)}V`
                            : featured.batteryLevel != null
                              ? `${Math.round(featured.batteryLevel)}%`
                              : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Posição</div>
                        <div className="truncate font-semibold">{address ?? "Buscando..."}</div>
                        <div className="truncate text-[11px] text-muted-foreground">
                          {timeWithAgo(featured.positionTimestamp ?? featured.lastConnection)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Link
                  to="/"
                  search={{ vehicle: featured.id }}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-bold text-primary-foreground hover:bg-primary/90"
                >
                  <MapPin className="h-4 w-4" /> Acessar rastreamento <ChevronRight className="h-4 w-4" />
                </Link>
              </CardContent>
            </Card>
          )}

          {canViewDevices && !vehiclesLoading && <FleetInsightsTop vehicles={vehicles} staff={!isClientAccount} />}
        </div>

        <div className="space-y-6">
          {featured && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">Resumo do seu veículo</CardTitle>
                <span className="text-xs font-semibold text-primary">Hoje</span>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <SummaryStat
                    icon={<Gauge className="h-4 w-4" />}
                    label="Distância"
                    value={`${(summary?.distanceKm ?? 0).toFixed(0)} km`}
                  />
                  <SummaryStat
                    icon={<RouteIcon className="h-4 w-4" />}
                    label="Paradas"
                    value={String(validStops.length)}
                  />
                  <SummaryStat
                    icon={<Zap className="h-4 w-4" />}
                    label="Vel. máxima"
                    value={`${summary?.maxSpeed ?? 0} km/h`}
                  />
                  <SummaryStat
                    icon={<Activity className="h-4 w-4" />}
                    label="Em movimento"
                    value={formatShortDuration(movingMs)}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setHistoryVehicleId(featured.id)}
                  className="flex w-full items-center justify-between text-sm font-semibold text-primary hover:underline"
                >
                  Ver histórico completo <ChevronRight className="h-4 w-4" />
                </button>
              </CardContent>
            </Card>
          )}

          {/* Pedido 2026-09-03: trocar o card de emergência (0800) pelo
              Street View do veículo em destaque, só na tela Início do web —
              PWA continua com seu próprio hero (ver mobile-app.tsx). */}
          {featured && Number.isFinite(featured.lat) && Number.isFinite(featured.lng) && (
            <Card className="overflow-hidden">
              <StreetViewPanorama
                lat={featured.lat}
                lng={featured.lng}
                heading={featured.bearing}
                className="h-40 w-full"
                fallback={
                  <div className="flex h-full w-full items-center justify-center bg-muted text-xs text-muted-foreground">
                    Sem imagem de rua disponível aqui
                  </div>
                }
              />
            </Card>
          )}

          {/* Equipe (admin/funcionário) não vê o Acesso rápido — pedido
              2026-10-14, o espaço foi pro Painel. */}
          {featured && isClientAccount && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">Acesso rápido</CardTitle>
                {/* Configuração é só do WEB — o "Acesso rápido" do app PWA
                    é fixo e nunca lê isso (ver InicioPanel em
                    mobile-app.tsx), pedido explícito: "só pode configurar
                    no web, no app pwa nunca pode mudar". */}
                <button
                  type="button"
                  onClick={() => setConfigOpen(true)}
                  aria-label="Configurar acesso rápido"
                  className="grid h-7 w-7 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <Settings2 className="h-4 w-4" />
                </button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {quickAccessTiles.map(({ key, node }) => (
                    <div key={key}>{node}</div>
                  ))}
                </div>
                {quickAccessTiles.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Nenhum atalho configurado.{" "}
                    <button type="button" onClick={() => setConfigOpen(true)} className="font-semibold text-primary hover:underline">
                      Configurar
                    </button>
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Painel com gráficos (pedido 2026-10-14) — substitui o antigo card
          "Status da frota"; clientes/acessos/vendas/problemas só pra equipe.
          Números e destaques ficam na coluna da esquerda (acima). */}
      {canViewDevices && !vehiclesLoading && <FleetInsightsCharts vehicles={vehicles} staff={!isClientAccount} />}

      <QuickAccessConfigDialog open={configOpen} onOpenChange={setConfigOpen} currentTiles={quickAccessKeys} />
      {featured && (canLock || canUnlock) && (
        <VehicleBlockConfirmDialog
          open={blockDialogOpen}
          onOpenChange={setBlockDialogOpen}
          vehicleName={featured.name}
          vehiclePlate={featured.plate}
          canLock={canLock}
          canUnlock={canUnlock}
          sending={featuredBlock.sending}
          onConfirm={(action) => void featuredBlock.send(action)}
        />
      )}

      <VehicleSendCommandDialog
        vehicle={commandVehicle}
        open={!!commandVehicle}
        onOpenChange={(open) => !open && setCommandVehicle(null)}
      />
      {historyVehicleId && (
        <RouteHistoryCenter
          vehicleId={historyVehicleId}
          vehicles={vehicles}
          onClose={() => setHistoryVehicleId(null)}
        />
      )}
    </div>
  );
}

function FleetStat({ label, value, className }: { label: string; value: number; className?: string }) {
  return (
    <div className="rounded-lg border bg-muted/30 p-3 text-center">
      <div className={cn("text-2xl font-bold", className)}>{value}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function SummaryStat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border bg-muted/30 p-3">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <div className="mt-1 text-sm font-bold">{value}</div>
    </div>
  );
}

function QuickAction({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border bg-muted/30 p-3 text-sm font-medium transition-colors hover:bg-muted/60">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
        {icon}
      </span>
      {label}
    </div>
  );
}

// Cada chave do catálogo (backend) vira um elemento navegável aqui — o
// backend só sabe label/descrição (texto), quem sabe pra onde cada um leva
// é sempre o frontend. Chave sem permissão (ex.: "users" sem
// devices.view/users.view) ou que dependa de um veículo em destaque que
// não existe simplesmente não aparece — nunca um botão quebrado/desabilitado
// sem explicação.
function renderQuickAccessTile(
  tileKey: string,
  ctx: {
    featured: Vehicle;
    canBlock: boolean;
    onBlock: () => void;
    canSendCommand: boolean;
    canViewUsers: boolean;
    canSeeAdmin: boolean;
    onSendCommand: () => void;
    onOpenHistory: () => void;
  },
): { key: string; node: ReactNode } | null {
  const { featured, canBlock, onBlock, canSendCommand, canViewUsers, canSeeAdmin, onSendCommand, onOpenHistory } = ctx;
  switch (tileKey) {
    case "block":
      if (!canBlock) return null;
      return {
        key: tileKey,
        node: (
          <button type="button" onClick={onBlock} className="w-full text-left">
            <QuickAction icon={<ShieldAlert className="h-4 w-4" />} label="Bloqueio veicular" />
          </button>
        ),
      };
    case "tracking":
      return {
        key: tileKey,
        node: (
          <Link to="/" search={{ vehicle: featured.id }}>
            <QuickAction icon={<MapPin className="h-4 w-4" />} label="Rastreamento em tempo real" />
          </Link>
        ),
      };
    case "send_command":
      if (!canSendCommand) return null;
      return {
        key: tileKey,
        node: (
          <button type="button" onClick={onSendCommand} className="w-full text-left">
            <QuickAction icon={<Lock className="h-4 w-4" />} label="Enviar comando" />
          </button>
        ),
      };
    case "route_history":
      return {
        key: tileKey,
        node: (
          <button type="button" onClick={onOpenHistory} className="w-full text-left">
            <QuickAction icon={<RouteIcon className="h-4 w-4" />} label="Histórico do veículo" />
          </button>
        ),
      };
    case "reports":
      return {
        key: tileKey,
        node: (
          <Link to="/reports">
            <QuickAction icon={<FileText className="h-4 w-4" />} label="Meus relatórios" />
          </Link>
        ),
      };
    case "geofences":
      return {
        key: tileKey,
        node: (
          <Link to="/geofences">
            <QuickAction icon={<LandPlot className="h-4 w-4" />} label="Gerenciar geocercas" />
          </Link>
        ),
      };
    case "vehicles":
      return {
        key: tileKey,
        node: (
          <Link to="/vehicles" search={{ new: undefined }}>
            <QuickAction icon={<Car className="h-4 w-4" />} label="Lista de veículos" />
          </Link>
        ),
      };
    case "users":
      if (!canViewUsers) return null;
      return {
        key: tileKey,
        node: (
          <Link to="/users">
            <QuickAction icon={<UsersIcon className="h-4 w-4" />} label="Usuários" />
          </Link>
        ),
      };
    case "admin":
      if (!canSeeAdmin) return null;
      return {
        key: tileKey,
        node: (
          <Link to="/admin">
            <QuickAction icon={<ShieldCheck className="h-4 w-4" />} label="Administração" />
          </Link>
        ),
      };
    default:
      return null;
  }
}

// Dialog de configuração — pedido 2026-09-03: "coloca uma opção aonde ele
// pode escolher o que quer colocar e descrição de cada coisa... isso não é
// pra ficar salvo em local[Storage], tem que ficar salvo no usuário mesmo
// do admin". Catálogo vem do backend (label+descrição, fonte única —
// nunca duplicado aqui); a persistência é sempre o PRÓPRIO usuário
// autenticado (ver useMyQuickAccess/useUpdateMyQuickAccess), nunca
// localStorage.
function QuickAccessConfigDialog({
  open,
  onOpenChange,
  currentTiles,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentTiles: string[];
}) {
  const { data: catalogData, isLoading: catalogLoading } = useQuickAccessCatalog();
  const updateMine = useUpdateMyQuickAccess();
  const [selected, setSelected] = useState<string[]>(currentTiles);

  // Reabre sempre com a seleção salva de verdade (não o que ficou de uma
  // edição cancelada da última vez que o dialog foi aberto).
  useEffect(() => {
    if (open) setSelected(currentTiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const catalog = catalogData?.catalog ?? [];
  const maxTiles = catalogData?.maxTiles ?? 6;

  const toggle = (key: string) => {
    setSelected((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= maxTiles) {
        toast.warning(`Máximo de ${maxTiles} atalhos`, {
          description: "Desmarque algum antes de escolher outro.",
        });
        return prev;
      }
      return [...prev, key];
    });
  };

  const handleSave = async () => {
    try {
      await updateMine.mutateAsync(selected);
      toast.success("Acesso rápido atualizado");
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao salvar o acesso rápido");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Configurar Acesso rápido</DialogTitle>
          <DialogDescription>
            Escolha até {maxTiles} atalhos para o card "Acesso rápido" da sua tela Início. Só vale
            para o web — o app do cliente (PWA) mantém o próprio conjunto fixo.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] space-y-1 overflow-y-auto">
          {catalogLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
          {catalog.map((tile) => {
            const checked = selected.includes(tile.key);
            return (
              <label
                key={tile.key}
                className="flex cursor-pointer items-start gap-3 rounded-lg p-2.5 hover:bg-muted/60"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggle(tile.key)}
                  className="mt-0.5"
                />
                <div className="min-w-0">
                  <div className="text-sm font-medium">{tile.label}</div>
                  <div className="text-xs text-muted-foreground">{tile.description}</div>
                </div>
              </label>
            );
          })}
        </div>
        <DialogFooter>
          <span className="mr-auto self-center text-xs text-muted-foreground">
            {selected.length}/{maxTiles} selecionados
          </span>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={updateMine.isPending}>
            {updateMine.isPending ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
